import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Space,
  Timeline,
  Card,
  Image,
  Collapse,
  Table,
  Modal,
  Button,
} from "antd";
import { ColumnsType } from "antd/es/table";
import {
  expandRowDownArrowIcon,
  expandRowRightArrowIcon,
  pickupTimelineIcon,
  dropoffTimelineIcon,
  homeIcon,
  rightArrow,
  arrowDownIcon,
  columnSortIcon,
} from "../../../../assets/images";
import Styles from "./DuplicateSchedule.module.scss";
import DashboardStyles from "../../../DashBoard/DashBoard.module.scss";
import DashBoardGmaps from "../../../DashBoard/DashBoardGmaps";
import moment from "moment";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import { GeneralDefinition } from "../../../../types/ApplicationTypes";
import getTripIcon from "../../../../helpers/getTripIcon";
import TripTimings from "../../../Shared/TripTimings";
import EllipsisText from "../../../../helpers/EllipsisText/EllipsisText";
import { usePlanDetailsCtx } from "../../../../context/PlanDetailsContext";
import { ITripDetails } from "../../../ServiceUsers/Interfaces/ITripDetails";
import { usePersonSummaryCtx } from "../../../../context/PersonSummaryContext";

type Props = {
  isVisible: boolean;
  onCancelDuplicateSchedule: () => void;
  data: ITripDetails[] | undefined;
  onContinueDuplicateSchedule: () => void;
};

interface TripsColumnType {
  tripDate: string;
  appointmentTime: string;
  serviceUser: string;
  tripCost: number;
  tripReason: string;
}

const DuplicateSchedule: React.FC<Props> = ({
  isVisible,
  onCancelDuplicateSchedule,
  data,
  onContinueDuplicateSchedule,
}) => {
  const { width } = useWindowSize();
  const [expandedPanelId, setIsExpandedPanelId] = useState<string[]>([]);
  const [isMapModalOpen, setIsMapModalOpen] = React.useState<boolean>(false);
  const { tripDetailsInfo } = usePlanDetailsCtx();
  const { isWandaConcierge } = usePersonSummaryCtx();

  const columns: ColumnsType<TripsColumnType> = [
    {
      title: (
        <Row className={Styles.dateColumnHeading}>
          <Col>Date</Col>
        </Row>
      ),
      key: "appointmentTime",
      width: 100,
      render: (record): JSX.Element => (
        <span className={Styles.TableText}>
          {moment(record.tripDate).format("MM/DD/YYYY")}
        </span>
      ),
    },
    {
      title: "Time",
      key: "appointmentTime",
      width: 100,
      ellipsis: true,
      render: (record): JSX.Element => (
        <span className={Styles.TableText}>
          {record.isWillCall
            ? "Call when ready"
            : moment(record.appointmentTime || record.requestPickTime).format(
                "hh:mm A"
              )}
        </span>
      ),
    },
    {
      title: "Service User",
      dataIndex: "serviceUser",
      key: "serviceUser",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Cost",
      key: "tripCost",
      render: (record): JSX.Element => (
        <span className={Styles.TableText}>
          ${record?.tripCost?.toFixed(2)}
        </span>
      ),
      width: 80,
    },
    {
      title: "Type",
      key: "tripReason",
      width: 100,
      render: (record): JSX.Element => (
        <Space className={Styles.TableText}>
          {getTripIcon(record.tripReason)}
          {record.tripReason || "Ride"}
        </Space>
      ),
    },
  ];

  const isExpanded = (key: string | string[]) =>
    Array.isArray(key) && setIsExpandedPanelId(key);

  return (
    <>
      {width > GeneralDefinition.MobileView ? (
        <Modal
          visible={isVisible}
          centered
          width={776}
          footer={
            <Row className={Styles.modalContainer}>
              <Col>
                <Row justify="center">
                  <Col>
                    <Row justify="center" className={Styles.titleSize}>
                      <Col span={24} className={Styles.topTitleBlue}>
                        {data?.length && data[0].firstName} already has a
                        service booked on{" "}
                        {moment(
                          moment(data?.length && data[0].tripDate).format(
                            "YYYY-MM-DD"
                          )
                        ).format("dddd")}
                        ,{" "}
                        {moment(data?.length && data[0].tripDate).format(
                          "MM/DD/YYYY"
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <div
                  id={DashboardStyles.tableWrapper}
                  className={DashboardStyles.tableHead}
                >
                  <Table
                    pagination={false}
                    columns={columns}
                    rowKey="bookingId"
                    showHeader
                    dataSource={data}
                    expandable={{
                      expandIcon: ({ expanded, onExpand, record }: any) => (
                        <Image
                          preview={false}
                          onClick={(e) => {
                            onExpand(record, e);
                          }}
                          src={
                            expanded
                              ? expandRowDownArrowIcon
                              : expandRowRightArrowIcon
                          }
                        />
                      ),
                      expandedRowRender: (record: any, i: number) => (
                        <Row key={i}>
                          <Col span={12}>
                            <TripTimings trip={record} />
                            <Row gutter={[0, 15]}>
                              <Col span={24} />
                              <Col span={24}>
                                <Space direction="vertical" size={0}>
                                  <Typography.Text
                                    className={Styles.descTitleBlue14}
                                  >
                                    Trip ID
                                  </Typography.Text>
                                  <Typography.Text
                                    className={Styles.descText12}
                                  >
                                    {record.tripId}
                                  </Typography.Text>
                                </Space>
                              </Col>
                              <Col span={24}>
                                <Space direction="vertical" size={0}>
                                  <Typography.Text
                                    className={Styles.descTitleBlue14}
                                  >
                                    Booking ID
                                  </Typography.Text>
                                  <Typography.Text
                                    className={Styles.descText12}
                                  >
                                    {record.bookingId}
                                  </Typography.Text>
                                </Space>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={12}>
                            <Card>
                              <Row align="middle">
                                <Col span={4} className={Styles.distanceText}>
                                  <Row
                                    justify="end"
                                    align="middle"
                                    className={Styles.distanceMargin}
                                  >
                                    <Col>
                                      <span>{record.providerDistance} mi</span>
                                    </Col>
                                  </Row>
                                </Col>
                                <Col
                                  span={20}
                                  className={Styles.customTimeline}
                                >
                                  <Timeline>
                                    <Timeline.Item
                                      dot={
                                        <Image
                                          preview={false}
                                          src={pickupTimelineIcon}
                                        />
                                      }
                                    >
                                      <Row>
                                        <Col>
                                          <Space direction="vertical" size={0}>
                                            <Typography.Text
                                              className={
                                                Styles.descTextBlueBold12
                                              }
                                            >
                                              Pickup Address
                                            </Typography.Text>
                                            <Typography.Text
                                              className={Styles.descTitle14}
                                            >
                                              <Image
                                                src={homeIcon}
                                                preview={false}
                                              />{" "}
                                              {record.pickUpAddress1}
                                            </Typography.Text>
                                            <Typography.Text
                                              className={Styles.descText12}
                                            >
                                              {record.pickUpCity},{" "}
                                              {record.pickUpState}{" "}
                                              {record.pickUpZipCode}
                                            </Typography.Text>
                                          </Space>
                                        </Col>
                                      </Row>
                                    </Timeline.Item>
                                    <Timeline.Item
                                      dot={
                                        <Image
                                          preview={false}
                                          src={dropoffTimelineIcon}
                                        />
                                      }
                                    >
                                      <Row>
                                        <Col>
                                          <Space direction="vertical" size={0}>
                                            <Typography.Text
                                              className={
                                                Styles.descTextBlueBold12
                                              }
                                            >
                                              Dropoff Address
                                            </Typography.Text>
                                            <Typography.Text
                                              className={Styles.descTitle14}
                                            >
                                              {record.dropOffAddress1}
                                            </Typography.Text>
                                            <Typography.Text
                                              className={Styles.descText12}
                                            >
                                              {record.dropOffCity},{" "}
                                              {record.dropOffState}{" "}
                                              {record.dropOffZipCode}
                                            </Typography.Text>
                                          </Space>
                                        </Col>
                                      </Row>
                                    </Timeline.Item>
                                  </Timeline>
                                </Col>
                              </Row>
                              <Row>
                                <Col>
                                  <DashBoardGmaps
                                    setIsMapModalOpen={setIsMapModalOpen}
                                    tripDetails={record}
                                    width={"281px"}
                                    height={"167px"}
                                  />
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                          <Modal
                            visible={isMapModalOpen}
                            footer={null}
                            className={`${DashboardStyles.fullScreenMap} ${DashboardStyles.fullScreenMapModal}`}
                          >
                            <DashBoardGmaps
                              isMapModalOpen={isMapModalOpen}
                              setIsMapModalOpen={setIsMapModalOpen}
                              tripDetails={record}
                              width={"807px"}
                              height={"501px"}
                            />
                          </Modal>
                        </Row>
                      ),
                    }}
                  />
                </div>
                {!isWandaConcierge ? (
                  <Row
                    justify="center"
                    gutter={[0, 50]}
                    className={Styles.footerText}
                  >
                    <Col span={24} />
                    <Col>
                      <Row justify="center">
                        <Col>
                          Please call us at{" "}
                          {tripDetailsInfo.wandaCustomerCareNumber} to schedule
                        </Col>
                      </Row>
                      <Row justify="center">
                        <Col>additional service on this day.</Col>
                      </Row>
                    </Col>
                    <Col span={24} />
                  </Row>
                ) : (
                  <Row
                    justify="center"
                    gutter={[0, 50]}
                    className={Styles.footerText}
                  >
                    <Col span={24} />
                    <Col>
                      <Row justify="center">
                        <Col>
                          Verify the time for the requested service does
                        </Col>
                      </Row>
                      <Row justify="center">
                        <Col>
                          not conflict with the existing booked service(s).
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24} />
                  </Row>
                )}
                <Row justify="center" gutter={[0, 50]}>
                  <Col className={Styles.modalBtn}>
                    <Button
                      className={
                        isWandaConcierge
                          ? Styles.buttonTheme
                          : Styles.buttonCancel
                      }
                      onClick={onCancelDuplicateSchedule}
                    >
                      Close
                    </Button>
                    {isWandaConcierge && (
                      <Button
                        className={Styles.buttonThemeBlue}
                        onClick={onContinueDuplicateSchedule}
                      >
                        Continue Booking
                      </Button>
                    )}
                  </Col>
                  <Col span={24} />
                </Row>
              </Col>
            </Row>
          }
        />
      ) : (
        <Modal
          className={Styles.mobileDuplicateModalContainer}
          visible={isVisible}
          centered={true}
          width="unset"
          footer=""
        >
          <Row
            justify="center"
            gutter={[0, 25]}
            className={Styles.mobileDuplicateModalHeader}
          >
            <Col className={Styles.mobileDuplicateModalTitle}>
              {data?.length && data[0].firstName} already has a service booked
              on{" "}
              {moment(
                moment(data?.length && data[0].tripDate).format("YYYY-MM-DD")
              ).format("dddd")}
              , {moment(data?.length && data[0].tripDate).format("MM/DD/YYYY")}
            </Col>
            <Col span={24} />
          </Row>
          <Collapse className={Styles.divider} onChange={isExpanded}>
            {data &&
              data.length > 0 &&
              data.map((trip: any) => (
                <Collapse.Panel
                  header={
                    <Row justify="center" align="middle">
                      <Col span={4} className={Styles.mobileTripIcon}>
                        {getTripIcon(
                          trip.tripReason ? trip.tripReason : "",
                          "35px"
                        )}
                      </Col>
                      <Col span={18}>
                        <Row>
                          <Col>
                            <Row>
                              <Col className={Styles.descTitleBold14}>
                                <EllipsisText
                                  text={trip.serviceUser}
                                  maxVisibleTextLength={20}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Typography.Text
                                  className={Styles.descTitleBold14}
                                >
                                  {trip.dropOffAddress1}
                                </Typography.Text>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Typography.Text className={Styles.descTitle14}>
                                  {moment(trip.tripDate).format("MM/DD/YYYY")} -{" "}
                                  {trip.isWillCall
                                    ? "Call when ready"
                                    : moment(
                                        trip.appointmentTime ||
                                          trip.requestPickTime
                                      ).format("hh:mm A")}
                                </Typography.Text>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Typography.Text className={Styles.descTitle14}>
                                  ${trip?.tripCost?.toFixed(2)}
                                </Typography.Text>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={2}>
                        {expandedPanelId.includes(trip.bookingId) ? (
                          <Image src={arrowDownIcon} preview={false} />
                        ) : (
                          <Image src={rightArrow} preview={false} />
                        )}
                      </Col>
                    </Row>
                  }
                  key={trip.bookingId}
                  showArrow={false}
                >
                  <Row className={Styles.expandedPanel}>
                    <Col span={24}>
                      <Row align="middle" gutter={[0, 15]}>
                        <Col span={24} />
                        <Col span={4} className={Styles.distanceText}>
                          <Row
                            justify="end"
                            align="middle"
                            className={Styles.distanceMargin}
                          >
                            <Col>
                              <span>{trip.providerDistance} mi</span>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={20} className={Styles.customTimeline}>
                          <Timeline>
                            <Timeline.Item
                              dot={
                                <Image
                                  preview={false}
                                  src={pickupTimelineIcon}
                                />
                              }
                            >
                              <Row>
                                <Col>
                                  <Space direction="vertical" size={0}>
                                    <Typography.Text
                                      className={Styles.descTextBlueBold12}
                                    >
                                      Pickup Address
                                    </Typography.Text>
                                    <Typography.Text
                                      className={Styles.descTitle14}
                                    >
                                      <Image src={homeIcon} preview={false} />{" "}
                                      {trip.pickUpAddress1}
                                    </Typography.Text>
                                    <Typography.Text
                                      className={Styles.descText12}
                                    >
                                      {trip.pickUpCity}, {trip.pickUpState}{" "}
                                      {trip.pickUpZipCode}
                                    </Typography.Text>
                                  </Space>
                                </Col>
                              </Row>
                            </Timeline.Item>
                            <Timeline.Item
                              dot={
                                <Image
                                  preview={false}
                                  src={dropoffTimelineIcon}
                                />
                              }
                            >
                              <Row>
                                <Col>
                                  <Space direction="vertical" size={0}>
                                    <Typography.Text
                                      className={Styles.descTextBlueBold12}
                                    >
                                      Dropoff Address
                                    </Typography.Text>
                                    <Typography.Text
                                      className={Styles.descTitle14}
                                    >
                                      {trip.dropOffAddress1}
                                    </Typography.Text>
                                    <Typography.Text
                                      className={Styles.descText12}
                                    >
                                      {trip.dropOffCity}, {trip.dropOffState}{" "}
                                      {trip.dropOffZipCode}
                                    </Typography.Text>
                                  </Space>
                                </Col>
                              </Row>
                            </Timeline.Item>
                          </Timeline>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[0, 12]}>
                        <Col>
                          {data && (
                            <DashBoardGmaps
                              setIsMapModalOpen={setIsMapModalOpen}
                              tripDetails={data[0]}
                              width="400px"
                              height={"167px"}
                            />
                          )}
                        </Col>
                        <Col span={24} />
                      </Row>
                    </Col>
                    <Col span={24}>
                      {trip.pickupNotes !== "" || trip.dropNotes !== "" ? (
                        <Row gutter={[0, 20]}>
                          <Col span={12} className={Styles.leftAlign}>
                            <Space direction="vertical" size={0}>
                              <Typography.Text
                                className={Styles.descTitleBlue14}
                              >
                                Pickup Instructions
                              </Typography.Text>
                              <Typography.Text className={Styles.descText14}>
                                {trip.pickupNotes}
                              </Typography.Text>
                            </Space>
                          </Col>
                          <Col span={12} className={Styles.leftAlign}>
                            <Space direction="vertical" size={0}>
                              <Typography.Text
                                className={Styles.descTitleBlue14}
                              >
                                Dropoff Instructions
                              </Typography.Text>
                              <Typography.Text className={Styles.descText14}>
                                {trip.dropNotes}
                              </Typography.Text>
                            </Space>
                          </Col>
                          <Col span={24} />
                        </Row>
                      ) : (
                        ""
                      )}
                    </Col>
                    <Col span={24}>
                      <Row className={Styles.collapsibleIdContent}>
                        <Col
                          span={12}
                          className={Styles.collapsibleContentTitle}
                        >
                          Trip ID
                        </Col>
                        <Col
                          span={12}
                          className={Styles.collapsibleContentTitle}
                        >
                          Booking ID
                        </Col>
                        <Col
                          span={12}
                          className={Styles.collapsibleContentText}
                        >
                          {trip.tripId}
                        </Col>
                        <Col
                          span={12}
                          className={Styles.collapsibleContentText}
                        >
                          {trip.bookingId}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Collapse.Panel>
              ))}
          </Collapse>
          {!isWandaConcierge ? (
            <Row
              justify="center"
              gutter={[0, 25]}
              className={Styles.mobileDuplicateModalFooter}
            >
              <Col span={24} />
              <Col className={Styles.mobileHelplineText}>
                Please call us at {tripDetailsInfo.wandaCustomerCareNumber} to
                schedule additional service on this day.
              </Col>
              <Col span={24} />
            </Row>
          ) : (
            <Row
              justify="center"
              gutter={[0, 25]}
              className={Styles.mobileDuplicateModalFooter}
            >
              <Col span={24} />
              <Col className={Styles.mobileHelplineText}>
                Verify the time for the requested service does not conflict with
                the existing booked service(s).
              </Col>
              <Col span={24} />
            </Row>
          )}
          <Row justify="center">
            <Col className={Styles.modalBtn}>
              <Row justify="center" className={Styles.cancelButtonSection}>
                <Button
                  className={
                    isWandaConcierge ? Styles.buttonTheme : Styles.buttonCancel
                  }
                  onClick={onCancelDuplicateSchedule}
                >
                  Close
                </Button>
              </Row>
              {isWandaConcierge && (
                <Row justify="center" className={Styles.cancelButtonSection}>
                  <Button
                    className={Styles.buttonThemeBlue}
                    onClick={onContinueDuplicateSchedule}
                  >
                    Continue Booking
                  </Button>
                </Row>
              )}
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export default DuplicateSchedule;
